/**
 * A failure whose `message` is already safe to show a user.
 *
 * WHY A TYPE AND NOT A CONVENTION. `PendingScope.save` signals failure by rejecting, and the
 * unsaved-changes dialog shows the rejection's message so the reader learns why their work could not
 * be saved. Every scope in the app already rejects with localized product copy — the company editors
 * throw `result.message` from `companyAdminErrorMessage`, BrandingPanel throws
 * `userErrorMessage(intl, e)` — but nothing said so and nothing checked, so the dialog was rendering
 * `e.message` off any Error at all. One scope that let an Apollo error escape would have put
 * genesis's untranslated prose on screen, which @adr SPARK-0010-no-raw-server-errors-in-ui@1 exists to prevent (jfalkenstein, !238
 * note 25773).
 *
 * Wrapping the render site in `userErrorMessage` was the obvious fix and is the wrong one: the
 * messages arriving there are ALREADY localized, so re-processing them would replace "You are the
 * last admin of this company, promote somebody else first" with a generic "Something went wrong".
 * The problem was never that the text was raw — it was that nothing distinguished the deliberate,
 * localized rejection from an accidental one.
 *
 * So the distinction is carried in the type. A `LocalizedError`'s message is shown; anything else
 * gets a generic fallback and goes to the console. A scope that forgets cannot leak — it can only
 * be less informative.
 */
export class LocalizedError extends Error {
  constructor(message: string, options?: ErrorOptions) {
    super(message, options);
    this.name = "LocalizedError";
  }
}

/**
 * The message to show for a caught rejection, or null when it carries nothing showable.
 *
 * Null means "use your own fallback": the caller knows the surface and can phrase it better than a
 * shared helper. Deliberately does NOT fall back to `userErrorMessage` — the raw error still needs
 * logging and a support code, which is that helper's job at the call site, not this one's.
 */
export function localizedMessageOf(error: unknown): string | null {
  return error instanceof LocalizedError && error.message.trim() ? error.message : null;
}
